import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Clock, User, Mail, Phone, FileText, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { format, isBefore, startOfDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    service_type: "",
    notes: ""
  });

  useEffect(() => {
    document.title = "Book HVAC Service Online | Schedule Appointment | Virgil's Heating & Air";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Schedule your HVAC service appointment online. Easy booking for AC repair, heating installation, maintenance, and emergency services in Greater Sacramento. Same-day service available.");
    }
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (e) {
        console.error("Error fetching services:", e);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (dateStr) => {
    try {
      const response = await axios.get(`${API}/appointments/available-slots/${dateStr}`);
      setAvailableSlots(response.data.available_slots);
      setSelectedSlot("");
    } catch (e) {
      console.error("Error fetching slots:", e);
      setAvailableSlots([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedSlot || !formData.service_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const appointmentData = {
        ...formData,
        appointment_date: format(selectedDate, "yyyy-MM-dd"),
        appointment_time: selectedSlot
      };
      
      await axios.post(`${API}/appointments`, appointmentData);
      toast.success("Appointment booked successfully! We'll contact you shortly to confirm.");
      setStep(4);
    } catch (e) {
      console.error("Error booking appointment:", e);
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    return isBefore(date, today);
  };

  const canProceedToStep2 = formData.service_type && formData.customer_name && formData.email && formData.phone;
  const canProceedToStep3 = selectedDate && selectedSlot;

  return (
    <div className="pt-36 min-h-screen bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a]" data-testid="booking-page">
      {/* Hero */}
      <section className="py-12 bg-[#101014] border-b border-white/10" data-testid="booking-hero">
        <div className="container-custom">
          <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Book Now</p>
          <h1 className="font-heading font-black text-4xl md:text-5xl gradient-text mb-4">
            SCHEDULE YOUR SERVICE
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl">
            Choose your service, pick a convenient time, and we'll handle the rest. 
            Fast, easy booking in just a few steps.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-[#101014] border-b border-white/10" data-testid="booking-progress">
        <div className="container-custom">
          <div className="flex justify-between max-w-2xl mx-auto">
            {[
              { num: 1, label: "Service & Info" },
              { num: 2, label: "Date & Time" },
              { num: 3, label: "Confirm" },
              { num: 4, label: "Complete" }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full font-heading font-bold text-lg transition-all ${
                  step >= s.num 
                    ? 'bg-gradient-to-r from-[#0066CC] to-[#1E40AF] text-white shadow-lg' 
                    : 'bg-white/10 text-gray-500'
                }`}>
                  {step > s.num ? <Check className="w-6 h-6" /> : s.num}
                </div>
                <span className={`ml-3 text-sm font-medium hidden sm:block ${
                  step >= s.num ? 'text-white' : 'text-gray-500'
                }`}>
                  {s.label}
                </span>
                {i < 3 && (
                  <div className={`w-12 md:w-24 h-1 mx-4 rounded-full ${
                    step > s.num ? 'bg-gradient-to-r from-[#0066CC] to-[#1E40AF]' : 'bg-white/10'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding" data-testid="booking-form-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Step 1: Service & Info */}
            {step === 1 && (
              <div className="animate-fade-in-up" data-testid="step-1">
                <div className="bg-[#101014] rounded-2xl shadow-xl p-8 border border-white/10">
                  <h2 className="font-heading font-bold text-2xl text-white mb-8">
                    Select Service & Enter Your Information
                  </h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Service Selection */}
                    <div className="space-y-6">
                      <div>
                        <Label className="text-white mb-2 block font-semibold">Select Service *</Label>
                        <Select value={formData.service_type} onValueChange={handleServiceChange}>
                          <SelectTrigger className="input-light h-14 text-lg" data-testid="service-select">
                            <SelectValue placeholder="Choose a service" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#101014] border-white/10">
                            {services.map((service) => (
                              <SelectItem 
                                key={service.id} 
                                value={service.name}
                                className="text-white hover:bg-[#1E6BFF]/10 text-lg py-3"
                              >
                                {service.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-6">
                      <div>
                        <Label className="text-white mb-2 block font-semibold flex items-center gap-2">
                          <User className="w-4 h-4 text-[#5b93ff]" /> Full Name *
                        </Label>
                        <Input
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          placeholder="Enter your name"
                          className="input-light h-14 text-lg"
                          data-testid="name-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block font-semibold flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#5b93ff]" /> Email Address *
                        </Label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          className="input-light h-14 text-lg"
                          data-testid="email-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block font-semibold flex items-center gap-2">
                          <Phone className="w-4 h-4 text-[#5b93ff]" /> Phone Number *
                        </Label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(707) 000-0000"
                          className="input-light h-14 text-lg"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!canProceedToStep2}
                      className="btn-primary text-lg px-8 py-6"
                      data-testid="next-step-1"
                    >
                      Next: Select Date & Time
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Date & Time */}
            {step === 2 && (
              <div className="animate-fade-in-up" data-testid="step-2">
                <div className="bg-[#101014] rounded-2xl shadow-xl p-8 border border-white/10">
                  <h2 className="font-heading font-bold text-2xl text-white mb-8">
                    Choose Your Preferred Date & Time
                  </h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Calendar */}
                    <div className="calendar-container p-6" data-testid="calendar-container">
                      <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-[#5b93ff]" />
                        Select Date
                      </h3>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateDisabled}
                        className="rounded-xl border border-white/10"
                        data-testid="booking-calendar"
                      />
                    </div>

                    {/* Time Slots */}
                    <div className="bg-white/5 rounded-2xl p-6" data-testid="time-slots-container">
                      <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[#5b93ff]" />
                        Available Times
                        {selectedDate && (
                          <span className="text-[#5b93ff] font-normal text-sm ml-2">
                            {format(selectedDate, "MMM d, yyyy")}
                          </span>
                        )}
                      </h3>
                      
                      {!selectedDate ? (
                        <div className="text-center py-12">
                          <CalendarDays className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                          <p className="text-gray-400">Please select a date first</p>
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-[#ff6a5c] font-medium">No slots available for this date.</p>
                          <p className="text-gray-400 text-sm mt-2">Please select another date.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedSlot(slot)}
                              className={`p-4 text-base font-semibold rounded-xl border-2 transition-all duration-200 ${
                                selectedSlot === slot
                                  ? 'bg-gradient-to-r from-[#0066CC] to-[#1E40AF] border-[#0066CC] text-white shadow-lg'
                                  : 'bg-[#101014] border-white/10 text-gray-300 hover:border-[#0066CC] hover:shadow-md'
                              }`}
                              data-testid={`time-slot-${slot.replace(/[:\s]/g, '-')}`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="btn-outline text-lg px-8 py-6"
                      data-testid="back-step-2"
                    >
                      <ArrowLeft className="mr-2 w-5 h-5" />
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!canProceedToStep3}
                      className="btn-primary text-lg px-8 py-6"
                      data-testid="next-step-2"
                    >
                      Next: Review & Confirm
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 3 && (
              <div className="animate-fade-in-up" data-testid="step-3">
                <div className="bg-[#101014] rounded-2xl shadow-xl p-8 border border-white/10">
                  <h2 className="font-heading font-bold text-2xl text-white mb-8">
                    Review & Confirm Your Appointment
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    {/* Appointment Details */}
                    <div className="bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a] rounded-2xl p-6">
                      <h3 className="font-heading font-bold text-lg text-white mb-4">Appointment Details</h3>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl">
                          <FileText className="w-5 h-5 text-[#5b93ff] mt-0.5" />
                          <div>
                            <p className="text-gray-400 text-sm">Service</p>
                            <p className="text-white font-semibold">{formData.service_type}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl">
                          <CalendarDays className="w-5 h-5 text-[#5b93ff] mt-0.5" />
                          <div>
                            <p className="text-gray-400 text-sm">Date</p>
                            <p className="text-white font-semibold">
                              {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl">
                          <Clock className="w-5 h-5 text-[#5b93ff] mt-0.5" />
                          <div>
                            <p className="text-gray-400 text-sm">Time</p>
                            <p className="text-white font-semibold">{selectedSlot}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="bg-white/5 rounded-2xl p-6">
                      <h3 className="font-heading font-bold text-lg text-white mb-4">Contact Information</h3>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 bg-[#101014] p-4 rounded-xl border border-white/10">
                          <User className="w-5 h-5 text-[#5b93ff] mt-0.5" />
                          <div>
                            <p className="text-gray-400 text-sm">Name</p>
                            <p className="text-white font-semibold">{formData.customer_name}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-[#101014] p-4 rounded-xl border border-white/10">
                          <Mail className="w-5 h-5 text-[#5b93ff] mt-0.5" />
                          <div>
                            <p className="text-gray-400 text-sm">Email</p>
                            <p className="text-white font-semibold">{formData.email}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-[#101014] p-4 rounded-xl border border-white/10">
                          <Phone className="w-5 h-5 text-[#5b93ff] mt-0.5" />
                          <div>
                            <p className="text-gray-400 text-sm">Phone</p>
                            <p className="text-white font-semibold">{formData.phone}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="mb-8">
                    <Label className="text-white mb-2 block font-semibold">Additional Notes (Optional)</Label>
                    <Textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Any special instructions or details about your service needs..."
                      className="input-light min-h-[120px] text-lg"
                      data-testid="notes-input"
                    />
                  </div>

                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="btn-outline text-lg px-8 py-6"
                      data-testid="back-step-3"
                    >
                      <ArrowLeft className="mr-2 w-5 h-5" />
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className="btn-primary text-lg px-10 py-6"
                      data-testid="confirm-booking-btn"
                    >
                      {isLoading ? "Booking..." : "Confirm Booking"}
                      <Check className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Complete */}
            {step === 4 && (
              <div className="animate-fade-in-up text-center py-12" data-testid="step-4">
                <div className="bg-[#101014] rounded-2xl shadow-xl p-12 border border-white/10">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-green-400 to-green-600 rounded-full mb-6 shadow-lg">
                    <Check className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="font-heading font-bold text-4xl gradient-text mb-4">
                    BOOKING CONFIRMED!
                  </h2>
                  <p className="text-gray-300 text-xl max-w-md mx-auto mb-8">
                    Thank you for choosing Virgil's Heating and Air. 
                    We'll contact you shortly to confirm your appointment details.
                  </p>
                  <div className="bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a] p-6 rounded-2xl max-w-md mx-auto mb-8">
                    <p className="text-gray-400 text-sm mb-1">Your Appointment</p>
                    <p className="text-white font-heading font-bold text-2xl">{formData.service_type}</p>
                    <p className="text-[#5b93ff] text-lg font-semibold">
                      {selectedDate && format(selectedDate, "EEEE, MMMM d")} at {selectedSlot}
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate("/")}
                    className="btn-primary text-lg px-10 py-6"
                    data-testid="back-home-btn"
                  >
                    Back to Home
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
